/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from "react";
import { isFunction, debounce } from "utils/lodashUtils";
import SlideShowViewCss from "./SlideShowView.css";
import * as stores from "stores";
import { EmptyPageContainer } from "containers/document/EmptyPageContainer";
import { observer } from "mobx-react";
import { isValidPageIndex } from "selectors/pageSelectors";

const noOp = () => {};
@observer
class SlideShowView extends Component {
  constructor(props) {
    super(props);
    this.onScroll = this.onScroll.bind(this);
    this.onScrollWrapper = debounce(this.onScroll, 250);
    this.oldScrollLeft = 0;
    this.pageArray = [];
    this.lastSnappedPageIndex = -1;
    this.alignmentTimer = null;
    this.momentumScrollTimer = null;
    this.possibleLandingPageIndex = -1;
    this.setCurrPageZoomLevelToFitPage();
    this.initiateGrid();
    this.updateGrid(-1, this.props.currPageIndexForSlideMode);
    this.onResize = this.onResize.bind(this);
    this.scrollPercent = 0;
  }

  _focus() {
    if (this.elementHandle) this.elementHandle.focus();
  }

  resetFlags(snappedElementIndex) {
    this.oldScrollLeft = this.props.viewportWidth * snappedElementIndex;
    this.alignmentTimer && window.clearTimeout(this.alignmentTimer);
    this.alignmentTimer = null;
    this.momentumScrollTimer && window.clearTimeout(this.momentumScrollTimer);
    this.momentumScrollTimer = null;
    this.possibleLandingPageIndex = -1;
  }

  updatePageIndex() {
    // find the nearest snapping point. Rounding off is required because page may stop not exactly at a snapping point
    const snappedElementIndex = Math.round(this.elementHandle.scrollLeft / this.elementHandle.offsetWidth);
    this.lastSnappedPageIndex = this.props.currPageIndexForSlideMode;
    stores.documentStore.setCurrPageIndexForSlideMode(snappedElementIndex);
    this.resetFlags(snappedElementIndex);
    this.scrollPercent = snappedElementIndex / this.props.pageCount;
    return snappedElementIndex;
  }

  handleMomentumScroll() {
    let snappedElementIndex = this.elementHandle.scrollLeft / this.elementHandle.offsetWidth;
    if (!Number.isInteger(snappedElementIndex)) {
      // snap position not yet reached; smoothen the animation by pre-loading the pages
      let snappedElementIndex = Math.round(this.elementHandle.scrollLeft / this.elementHandle.offsetWidth);
      if (snappedElementIndex === this.possibleLandingPageIndex) {
        // update grid with this page
        this.lastSnappedPageIndex = this.props.currPageIndexForSlideMode;
        stores.documentStore.setCurrPageIndexForSlideMode(snappedElementIndex);
        this.scrollPercent = snappedElementIndex / this.props.pageCount;
      } else {
        this.possibleLandingPageIndex = snappedElementIndex;
      }
    } else {
      // snap position reached; stop all timers. Grid has already been updated in previous scroll event
      stores.documentStore.setCurrPageIndexForSlideMode(snappedElementIndex);
      this.scrollPercent = snappedElementIndex / this.props.pageCount;
      this.resetFlags(snappedElementIndex);
    }
  }

  onResize() {
    this.elementHandle.scrollLeft = this.scrollPercent * this.elementHandle.scrollWidth;
    this.oldScrollLeft = this.elementHandle.scrollLeft;
    this.lastSnappedPageIndex = this.props.currPageIndexForSlideMode;
  }

  onScroll() {
    const distanceMoved = Math.abs(this.elementHandle.scrollLeft - this.oldScrollLeft);
    if (distanceMoved === 0) {
      return;
    } else if (distanceMoved === this.props.viewportWidth) {
      // single page snapped. Simply update the page index, grid will update in render function and it will be done
      this.updatePageIndex();
    } else {
      // scroll position is more/less than one viewport width. Case of momentum scroll
      // clear alignment timer from previous scroll event
      if (this.alignmentTimer) {
        window.clearTimeout(this.alignmentTimer);
        this.alignmentTimer = null;
      }

      // restart timer for manual snapping the page in case user interrupts in between the momentum scroll and page is stopped midway
      this.alignmentTimer = window.setTimeout(() => {
        if (distanceMoved > 0.5 * this.props.viewportWidth) {
          this.elementHandle.scrollLeft = this.props.viewportWidth * this.updatePageIndex();
        } else {
          this.elementHandle.scrollLeft = this.props.viewportWidth * this.props.currPageIndexForSlideMode;
          this.resetFlags(this.props.currPageIndexForSlideMode);
        }
      }, 300);

      // start timer for animation smoothening
      if (distanceMoved > this.props.viewportWidth && !this.momentumScrollTimer) {
        this.lastSnappedPageIndex = this.props.currPageIndexForSlideMode;
        this.momentumScrollTimer = window.setInterval(this.handleMomentumScroll.bind(this), 100);
      }
    }
  }

  createPage(pageIndex) {
    const PageContainer = this.props.PageContainer;
    return <PageContainer pageIndex={pageIndex} />;
  }

  createEmptyPage(pageIndex) {
    return <EmptyPageContainer index={pageIndex} key={pageIndex} />;
  }

  setCurrPageZoomLevelToFitPage() {
    const fitPageZoomLevel = stores.zoom.getFitPageZoomLevel(this.props.currPageIndexForSlideMode);
    stores.zoom.setPageZoomLevel(fitPageZoomLevel);
  }

  initiateGrid() {
    const { pageCount } = this.props;
    if (pageCount < 5) {
      for (let i = 0; i < pageCount; i++) {
        this.pageArray.push(this.createPage(i));
      }
    } else {
      for (let i = 0; i < pageCount; i++) {
        this.pageArray.push(this.createEmptyPage(i));
      }
    }
  }

  updateGrid(oldIndex, newIndex) {
    const { pageCount } = this.props;
    if (oldIndex === newIndex || pageCount < 5) return;

    let pagesToBeRemoved = [],
      pagesToBeAdded = [];
    if (oldIndex !== -1) {
      // we have some previously focussed page. we need to remove corresponding pages
      pagesToBeRemoved.push(oldIndex - 2, oldIndex - 1, oldIndex, oldIndex + 1, oldIndex + 2);
    }

    // find the new pages which need to be added
    pagesToBeAdded.push(newIndex - 2, newIndex - 1, newIndex, newIndex + 1, newIndex + 2);

    // find the pages which are same in both lists; remove them from individual lists
    for (let i = 0; i < pagesToBeRemoved.length; i++) {
      const index = pagesToBeAdded.indexOf(pagesToBeRemoved[i]);
      if (index !== -1) {
        // same page no exists in both pagesToBeRemoved and pagesToBeAdded list.
        pagesToBeRemoved[i] = -1;
        pagesToBeAdded.splice(index, 1);
      }
    }
    pagesToBeRemoved = pagesToBeRemoved.filter(index => isValidPageIndex(stores, index));
    pagesToBeAdded = pagesToBeAdded.filter(index => isValidPageIndex(stores, index));

    // now handle both lists independantly.
    // first convert pages from pagesToBeRemoved to empty page
    for (let i = 0; i < pagesToBeRemoved.length; i++) {
      const index = pagesToBeRemoved[i];
      this.pageArray[index] = this.createEmptyPage(index);
    }

    // now convert empty pages from pagesToBeAdded to actual pages
    for (let i = 0; i < pagesToBeAdded.length; i++) {
      const index = pagesToBeAdded[i];
      this.pageArray[index] = this.createPage(index);
    }
  }

  componentDidMount() {
    if (this.elementHandle) {
      this.props.onPagesInViewChange({
        start: this.props.currPageIndexForSlideMode,
        end: this.props.currPageIndexForSlideMode
      });
      if (this.props.setFocusMethod) {
        this.props.setFocusMethod(this._focus.bind(this));
      }
      this.lastSnappedPageIndex = this.props.currPageIndexForSlideMode;
      this.elementHandle.scrollLeft = this.props.currPageIndexForSlideMode * this.props.viewportWidth;
      this.oldScrollLeft = this.props.viewportWidth * this.props.currPageIndexForSlideMode;
      this.scrollPercent = this.props.currPageIndexForSlideMode / this.props.pageCount;

      window.addEventListener("resize", this.onResize);
      window.addEventListener("orientationchange", this.onResize);
      this.elementHandle.addEventListener("scroll", this.onScrollWrapper);
    }
  }

  componentWillUnmount() {
    if (isFunction(this.props.onUnmount)) {
      this.props.onUnmount();
    }
    this.alignmentTimer && window.clearTimeout(this.alignmentTimer);
    this.momentumScrollTimer && window.clearTimeout(this.momentumScrollTimer);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("orientationchange", this.onResize);
    this.elementHandle.removeEventListener("scroll", this.onScrollWrapper);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.currPageIndexForSlideMode !== this.props.currPageIndexForSlideMode) {
      this.setCurrPageZoomLevelToFitPage();
      const requiredScrollLeft = this.props.currPageIndexForSlideMode * this.props.viewportWidth;
      if (!this.momentumScrollTimer) {
        this.elementHandle.scrollLeft = requiredScrollLeft;
      }

      this.props.onPagesInViewChange({
        start: this.props.currPageIndexForSlideMode,
        end: this.props.currPageIndexForSlideMode
      });
      this.lastSnappedPageIndex = this.props.currPageIndexForSlideMode;
    }
    if (prevProps.viewportWidth !== this.props.viewportWidth) {
      this.onResize();
    }
  }

  render() {
    if (this.lastSnappedPageIndex !== this.props.currPageIndexForSlideMode) {
      this.scrollPercent = this.props.currPageIndexForSlideMode / this.props.pageCount;
      this.updateGrid(this.lastSnappedPageIndex, this.props.currPageIndexForSlideMode);
    }

    const style = {
      height: this.props.parentViewHeight
    };
    if (this.props.bgColor) {
      style.backgroundColor = this.props.bgColor;
    }

    return (
      <div
        className={SlideShowViewCss.SlideShowView}
        style={style}
        onKeyDown={this.props.onKeyDown}
        onMouseDown={this.props.onMouseDown}
        onClick={this.props.onClick}
        ref={handle => {
          this.elementHandle = handle;
          this.props.setElementHandle(handle);
        }}
      >
        {this.pageArray}
      </div>
    );
  }
}

SlideShowView.defaultProps = {
  onClick: noOp,
  setElementHandle: noOp,
  customScrollEnd: noOp,
  margins: {
    left: "auto",
    right: "auto"
  }
};

export { SlideShowView };
